import { Box, Typography, useTheme, type TypographyProps } from "@carrotfertility/carotene-core"
import React from "react"
import { Skeleton, type SkeletonProps } from "./Skeleton"

// A line of the inherited type, MUI's text-skeleton trick, so the frame's height is the role's own
// line box at every breakpoint rather than a number that matches one of them.
const ONE_LINE = { "&::before": { content: '"\\00a0"' } }

type TextSkeletonProps = Pick<SkeletonProps, "width"> & { variant: TypographyProps["variant"] }

/** A placeholder one line of `variant` tall, for a frame standing in for a line of text. */
export function TextSkeleton({ variant, width }: TextSkeletonProps): JSX.Element {
  return (
    <Typography variant={variant} component="div">
      <Skeleton width={width} sx={ONE_LINE} />
    </Typography>
  )
}

// Transparent text drawn as one bar per line it wraps to.
const LINE_BARS = { display: "inline", boxDecorationBreak: "clone", WebkitBoxDecorationBreak: "clone" } as const

/**
 * A placeholder for a paragraph whose line count depends on the frame's width: `text` is laid out,
 * transparent, in `variant`, so it wraps to the same lines as the paragraph it stands in for, each
 * line drawn as its own bar.
 */
export function ParagraphSkeleton({
  variant,
  text
}: {
  variant: TypographyProps["variant"]
  text: string
}): JSX.Element {
  return (
    <Typography variant={variant} component="div" sx={{ color: "transparent" }}>
      <Skeleton sx={LINE_BARS}>{text}</Skeleton>
    </Typography>
  )
}

/**
 * A placeholder for carotene's `Link` with an end icon, laid out as that `Link` lays itself out:
 * `label` and a small icon's box, both middle-aligned, which stands each line taller than its role's
 * line box, so the frame wraps where the link does in a narrow column. `body1` is carotene's default
 * `Link` variant.
 *
 * Not balanced, though that `Link` balances its lines by default: a caller's `sx` replaces the
 * `Link`'s own, `textWrap` with it, and every caller of this frame passes one.
 *
 * ⚠ RE-CHECK THIS ON A CAROTENE UPGRADE. It copies that `Link`'s layout rather than rendering it.
 * Verified against carotene-core 4.1.0.
 */
export function IconLinkSkeleton({ label }: { label: string }): JSX.Element {
  const theme = useTheme()
  return (
    <Typography
      variant="body1"
      component="div"
      sx={{
        color: "transparent",
        "& > *": { verticalAlign: "middle", marginInlineEnd: theme.spacing(theme.tokens.spacing.xxxs) }
      }}
    >
      <Skeleton sx={LINE_BARS}>{label}</Skeleton>
      {/* MUI's `small` icon. */}
      <Box component="span" sx={{ display: "inline-block", inlineSize: "1.25rem", blockSize: "1.25rem" }} />
    </Typography>
  )
}
